import { AriaTextFieldOptions, useTextField } from '@react-aria/textfield';
import { forwardRef, ReactNode, RefObject, useRef } from 'react';
import { useMergeRefs } from '../../hooks/useMergeRefs';
import { Box } from '../Box';
import { Flex } from '../Flex';
import { Label } from '../Label';
import { Text } from '../Text';
import { Input, InputProps } from '.';

export interface InputFieldProps extends InputProps {
  /** Input helper text */
  description?: string;
  /** Input label text */
  label?: ReactNode;
  /** Input element css */
  inputCss?: InputProps['css'];
}

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      disabled,
      readOnly,
      required,
      leftIcon,
      rightIcon,
      error,
      description,
      label,
      css,
      size,
      inputCss,
      className,
      ...rest
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLInputElement>();
    const combinedRef = useMergeRefs(internalRef, ref);

    const { labelProps, errorMessageProps, descriptionProps } = useTextField(
      {
        label,
        isDisabled: disabled,
        isReadOnly: readOnly,
        isRequired: required,
        validationState: error ? 'invalid' : 'valid',
        ...(rest as AriaTextFieldOptions<'input'>),
      },
      internalRef as RefObject<HTMLInputElement>,
    );

    return (
      <Box css={css} className={className}>
        {label && typeof label === 'string' ? (
          <Label
            css={{
              mb: 8,
            }}
            {...labelProps}
            required={required}
          >
            {label}
          </Label>
        ) : (
          label
        )}
        <Input
          aria-labelledby={labelProps?.id}
          css={inputCss}
          ref={combinedRef}
          size={size}
          readOnly={readOnly}
          disabled={disabled}
          required={required}
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          error={error}
          {...rest}
        />
        <Flex
          className='betterleap-ui-input-error'
          css={{ minHeight: 20 }}
          align='end'
        >
          {description && !error && (
            <Text
              css={{
                color: '$text-lightest',
                fontSize: '$sm',
              }}
              noBreak
              {...descriptionProps}
            >
              {description}
            </Text>
          )}
          {error && (
            <Text
              css={{
                color: '$text-danger',
                fontSize: '$sm',
              }}
              noBreak
              {...errorMessageProps}
            >
              {error}
            </Text>
          )}
        </Flex>
      </Box>
    );
  },
);

InputField.displayName = 'InputField';
